import { cn } from '@/utils'
import { useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useCADQuery } from '@/services/queries/cads'
import { useDndContext } from '@dnd-kit/core'
import { useSelectable } from '@/pages/CADPage/hooks'
import { useIsReadOnly } from '@/pages/CADPage/hooks/useIsReadOnly'
import { Skeleton } from '@/components/ui/skeleton'
import { ProjectDocumentTitle } from '@/components/core/ProjectDocumentTitle'
import { CADVersionDropdown } from '../CADVersionDropdown'
import { DraggableTreeNodeOverlay } from './DraggableTreeNodeOverlay'
import { SubAssemblyTree } from './SubAssemblyTree'
import { HideAllPartsButton } from './HideAllPartsButton'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { ASMTreeNode, useCADPageStore } from '@/state'
import { ToggleAssemblyTreeButton } from '../ToggleAssemblyTreeButton'
import { TreeNodes } from './TreeNodes'

const normalize = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

export const AssemblyTree = () => {
  const { data: readOnlyData } = useIsReadOnly()
  const isReadOnly = readOnlyData.isReadOnly

  const { isLoading: isCadDataLoading, data: cadData } = useCADQuery()

  const [searchQuery, setSearchQuery] = useState('')
  const tree = useMemo(
    () => cadData?.rawAssemblyTree,
    [cadData?.rawAssemblyTree],
  )

  const selectable = useSelectable({})

  const selectedPartsCount = useCADPageStore(
    (state) => state.selectedParts.length,
  )

  const filteredTree = useMemo(() => {
    if (!tree || !searchQuery) return tree

    const nodes = tree?.nodes ?? []
    const idToNode = new Map<string, ASMTreeNode>()

    nodes.forEach((node) => {
      idToNode.set(node.uuid, { ...node })
    })

    const allChildIds = new Set(nodes.flatMap((n) => n.children))
    const rootIds = nodes
      .filter((n) => !allChildIds.has(n.uuid))
      .map((n) => n.uuid)

    const includedIds = new Set<string>()
    const normalizedQuery = normalize(searchQuery)

    function includeAllDescendants(id: string) {
      const stack = [id]
      while (stack.length) {
        const currentId = stack.pop()!
        if (includedIds.has(currentId)) continue
        includedIds.add(currentId)
        const currentNode = idToNode.get(currentId)
        if (currentNode) {
          stack.push(...currentNode.children)
        }
      }
    }

    function shouldIncludeNode(id: string): boolean {
      const node = idToNode.get(id)
      if (!node) return false

      const nameMatches = normalize(node.display_name).includes(normalizedQuery)
      let anyChildMatches = false

      for (const childId of node.children) {
        if (shouldIncludeNode(childId)) {
          anyChildMatches = true
        }
      }

      if (nameMatches) {
        includeAllDescendants(id)
        return true
      } else if (anyChildMatches) {
        includedIds.add(id)
        return true
      }

      return false
    }

    for (const rootId of rootIds) {
      shouldIncludeNode(rootId)
    }

    return {
      ...tree,
      nodes: nodes.filter((n) => includedIds.has(n.uuid)),
    }
  }, [tree, searchQuery])

  const gltf = cadData.gltf

  const isLoading = isCadDataLoading

  const isRetakeModeActive = useCADPageStore(
    useShallow((state) => state.isRetakeModeActive),
  )
  const dndContext = useDndContext()
  const isDragging = useMemo(
    () => Boolean(dndContext.activeNode),
    [dndContext.activeNode],
  )

  return (
    <div
      className={cn(
        'bg-white flex flex-col h-full w-full px-2 space-y-2.5 relative',
        { 'opacity-40 pointer-events-none': isRetakeModeActive },
      )}
    >
      <div className="py-2 px-8 border border-gray-300 rounded-xl w-full">
        <ProjectDocumentTitle isReadOnly={isReadOnly} />
      </div>

      <div className="border rounded-xl flex flex-col flex-grow overflow-hidden border-gray-300 border-2">
        <div className="flex flex-col border-b pb-2">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="text-base font-semibold px-4">Assembly Tree</div>
            <div className="flex items-center">
              {tree && gltf && (
                <HideAllPartsButton
                  nodes={tree.nodes.filter((node) => node.uuid !== tree.root)}
                  gltf={gltf}
                />
              )}
              <ToggleAssemblyTreeButton />
            </div>
          </div>
          <div className="flex mt-2 justify-center relative px-4 items-center pb-2">
            <Input
              type="text"
              className="p-1 px-2 pl-5 h-7 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-5 text-gray-400" />
          </div>

          <div className="text-xs font-semibold flex items-center justify-between px-4">
            <div>{selectedPartsCount} Parts Selected</div>
            <Button
              variant="ghost"
              className="py-0 px-4 text-xs font-semibold"
              onClick={() => {
                selectable.reset()
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        <div
          data-testid="assembly-tree"
          className={cn(
            'flex flex-col h-full px-0 py-2 text-sm font-regular pt-0',
            {
              'overflow-y-auto': !isDragging,
              'overflow-y-hidden': isDragging,
            },
          )}
        >
          {isLoading && (
            <div className="flex flex-col space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="w-full h-6 bg-gray-200" />
              ))}
            </div>
          )}
          {!isLoading && filteredTree && gltf && (
            <SubAssemblyTree tree={filteredTree} gltf={gltf} />
          )}

          {!isLoading && filteredTree && gltf && (
            <TreeNodes tree={filteredTree} gltf={gltf} />
          )}

          <DraggableTreeNodeOverlay />
        </div>
      </div>

      <CADVersionDropdown />
    </div>
  )
}
